// Wizard step styles using Plexus design system tokens
export const WIZARD_COLORS = {
  // Primary brand colors
  primary: 'var(--plexus-color-brand-400)', // Blue for primary actions
  primaryHover: 'var(--plexus-color-brand-500)', // Blue hover
  primaryActive: 'var(--plexus-color-positive-400)', // Green for active/completed states
  
  // Neutral colors
  neutral: 'var(--plexus-color-neutral-500)',
  neutralLight: 'var(--plexus-color-neutral-600)', // Light gray for inactive steps background
  neutralDark: 'var(--plexus-color-neutral-100)', // Dark text for readability
  
  // Brand colors
  brand: 'var(--plexus-color-brand-400)',
  brandAccent: 'var(--plexus-color-brand-500)',
  
  // Base colors
  white: 'var(--plexus-color-surface-50)',
  
  // Semantic colors using Plexus tokens
  border: 'var(--plexus-color-neutral-600)', // Use lighter neutral for subtle borders
  text: 'var(--plexus-color-body-text)',
  textInverse: 'var(--plexus-color-surface-50)',
  background: 'var(--plexus-color-body-bg)',
  surface: 'var(--plexus-color-surface-50)',
  error: 'var(--plexus-color-urgent-400)',
} as const;

export const WIZARD_STYLES = {
  // Main container styles
  wizardContainer: {
    maxWidth: '1000px',
    margin: '0 auto',
    paddingBottom: '120px', // Account for fixed bottom navigation
    paddingLeft: 'var(--plexus-space-md, 1rem)',
    paddingRight: 'var(--plexus-space-md, 1rem)',
  } as React.CSSProperties,

  // Demo callout styles
  demoContainer: {
    marginBottom: '2rem',
  },

  // Main content card styles
  contentCard: {
    backgroundColor: 'var(--plexus-color-surface-50)',
    borderRadius: 'var(--plexus-space-xs)',
    border: 'var(--plexus-border-width-thin) solid var(--plexus-color-neutral-600)', // Subtle border
    overflow: 'hidden',
    marginBottom: 'var(--plexus-space-lg)',
  },

  // Header styles
  header: {
    backgroundColor: 'var(--plexus-color-surface-100)',
    padding: 'var(--plexus-space-lg) var(--plexus-space-xl)',
  },

  headerContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },

  headerTitle: {
    fontWeight: 600,
    color: WIZARD_COLORS.text,
    margin: 0,
    fontSize: '1.5rem',
  },

  // Step content styles
  stepContent: {
    padding: 'var(--plexus-space-xl)',
    backgroundColor: 'var(--plexus-color-surface-50)',
  },

  stepTransition: {
    opacity: 1,
    transition: 'opacity 0.3s ease-in-out',
  },

  // Fixed bottom navigation styles
  fixedBottomNav: {
    position: 'fixed' as const,
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
    backgroundColor: 'var(--plexus-color-surface-50)',
    borderTop: '1px solid var(--plexus-color-neutral-500)',
    padding: 'var(--plexus-space-md) var(--plexus-space-xl)',
  },

  bottomNavContent: {
    maxWidth: '1000px',
    margin: '0 auto',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },

  // Progress steps styles
  progressContainer: {
    display: 'flex',
    gap: '8px',
    flex: 1,
    overflow: 'hidden',
    position: 'relative',
  },

  progressScrollContainer: {
    display: 'flex',
    gap: '8px',
    overflowX: 'auto',
    overflowY: 'hidden',
    scrollbarWidth: 'none', // Firefox
    msOverflowStyle: 'none', // IE/Edge
    flex: 1,
    padding: '0 20px', // Space for scroll indicators
  },

  progressScrollContainerWebkit: {
    // Webkit scrollbar hiding is handled via CSS classes
  },

  scrollIndicator: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '20px',
    height: '20px',
    backgroundColor: WIZARD_COLORS.white,
    border: `1px solid ${WIZARD_COLORS.border}`,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    zIndex: 10,
    boxShadow: 'var(--plexus-shadow-sm)', // TODO: Updated to Plexus shadow token
    transition: 'all 0.2s ease',
  },

  scrollIndicatorLeft: {
    left: '0',
  },

  scrollIndicatorRight: {
    right: '0',
  },

  scrollIndicatorHidden: {
    opacity: 0,
    pointerEvents: 'none',
  },

  // Individual step styles - Pill-shaped buttons
  stepBase: {
    flex: 1,
    padding: 'var(--plexus-space-md) var(--plexus-space-md)',
    borderRadius: 'var(--plexus-space-xs)', // 4px
    transition: 'all 0.2s ease-in-out',
    fontSize: '0.875rem',
    fontWeight: 500,
    textAlign: 'center' as const,
    minWidth: '120px',
    whiteSpace: 'nowrap',
    border: 'var(--plexus-border-width-thin) solid transparent',
  },

  stepBaseScrollable: {
    flex: 'none',
    padding: 'var(--plexus-space-md) var(--plexus-space-md)',
    borderRadius: 'var(--plexus-space-xs)', // 4px
    transition: 'all 0.2s ease-in-out',
    fontSize: '0.875rem',
    fontWeight: 500,
    textAlign: 'center' as const,
    minWidth: '120px',
    whiteSpace: 'nowrap',
    border: 'var(--plexus-border-width-thin) solid transparent',
  },

  stepInactive: {
    backgroundColor: 'var(--plexus-color-neutral-600)', // Light subtle background (#E0E0E3)
    color: 'var(--plexus-color-neutral-100)', // Dark text for readability (#26262C)
    fontWeight: 500, // Medium weight for better visibility
    cursor: 'default',
  },

  stepActive: {
    backgroundColor: 'var(--plexus-color-positive-400)', // Green for active state
    color: 'var(--plexus-color-surface-50)', // White text
    fontWeight: 600,
    cursor: 'pointer',
  },

  stepCompleted: {
    backgroundColor: 'var(--plexus-color-positive-400)', // Green for completed state
    color: 'var(--plexus-color-surface-50)', // White text
    fontWeight: 600,
    cursor: 'pointer',
  },

  // Footer navigation styles
  footerNavContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '32px',
    gap: '16px',
    flexWrap: 'wrap' as const,
  },

  footerNavBorder: {
    paddingTop: '1rem',
    borderTop: `1px solid ${WIZARD_COLORS.border}`,
  },
} as const;

// Helper function to get step style based on state
export const getStepStyle = (isActive: boolean, isCompleted: boolean, isClickable: boolean, isScrollable: boolean = false) => {
  const baseStyle = isScrollable ? { ...WIZARD_STYLES.stepBaseScrollable } : { ...WIZARD_STYLES.stepBase };
  
  if (isActive) {
    return { ...baseStyle, ...WIZARD_STYLES.stepActive };
  } else if (isCompleted) {
    return { ...baseStyle, ...WIZARD_STYLES.stepCompleted };
  } else {
    return { ...baseStyle, ...WIZARD_STYLES.stepInactive };
  }
};

// Helper function for hover effects
export const getStepHoverStyle = (isActive: boolean, isCompleted: boolean) => {
  if (isActive || isCompleted) {
    return {
      backgroundColor: 'var(--plexus-color-positive-300)', // Darker green on hover
      transform: 'translateY(-1px)',
    };
  }
  return {
    backgroundColor: 'var(--plexus-color-neutral-500)', // Slightly darker neutral hover for inactive steps
    transform: 'translateY(-1px)',
  };
};

export const getStepLeaveStyle = (isActive: boolean, isCompleted: boolean) => {
  if (isActive || isCompleted) {
    return {
      backgroundColor: 'var(--plexus-color-positive-400)', // Return to green
      transform: 'translateY(0)',
    };
  }
  return {
    backgroundColor: 'var(--plexus-color-neutral-600)', // Return to light neutral (#E0E0E3)
    transform: 'translateY(0)',
  };
};
